import type { MaskRuleConfig, MaskStrategy } from "../contracts/types";
import type { FieldProfile } from "../domain/profile";
import type { FieldCoverage } from "../domain/coverage";

interface FieldRulesTableProps {
  fieldProfiles: FieldProfile[];
  effectiveRules: MaskRuleConfig[];
  perFieldCoverage: FieldCoverage[];
  onChangeRule: (field: string, patch: Partial<Pick<MaskRuleConfig, "strategy" | "param">>) => void;
}

const STRATEGY_LABELS: Record<MaskStrategy, string> = {
  preserve: "Conservar",
  redact: "Redactar",
  hash: "Hash (consistente)",
  randomize: "Aleatorizar",
  generalize: "Generalizar",
};

export function FieldRulesTable({ fieldProfiles, effectiveRules, perFieldCoverage, onChangeRule }: FieldRulesTableProps) {
  if (fieldProfiles.length === 0) {
    return <p className="empty-state">Aún no hay campos perfilados. Ejecuta el escenario para verlos aquí.</p>;
  }

  const ruleByField = new Map(effectiveRules.map((rule) => [rule.field, rule]));
  const coverageByField = new Map(perFieldCoverage.map((entry) => [entry.field, entry]));

  return (
    <div style={{ overflowX: "auto" }}>
      <table className="coverage-table">
        <caption>Campos detectados, categoría de dato personal y regla de máscara aplicada</caption>
        <thead>
          <tr>
            <th scope="col">Campo</th>
            <th scope="col">Tipo</th>
            <th scope="col">Categoría</th>
            <th scope="col">Confianza</th>
            <th scope="col">Candidato PII</th>
            <th scope="col">Regla</th>
            <th scope="col">Cubierto</th>
          </tr>
        </thead>
        <tbody>
          {fieldProfiles.map((profile) => {
            const rule = ruleByField.get(profile.name);
            const coverage = coverageByField.get(profile.name);
            const fieldId = `rule-${profile.name}`;
            return (
              <tr key={profile.name}>
                <th scope="row">
                  <code>{profile.name}</code>
                  <div className="field-hint">{profile.reason}</div>
                </th>
                <td>{profile.inferredType}</td>
                <td>{profile.category}</td>
                <td>{profile.confidence}</td>
                <td>
                  {profile.piiCandidate ? (
                    <span className="state-badge state-badge--warning">Candidato</span>
                  ) : (
                    <span className="state-badge state-badge--info">No candidato</span>
                  )}
                </td>
                <td>
                  <label className="visually-hidden" htmlFor={fieldId}>
                    Estrategia de máscara para {profile.name}
                  </label>
                  <select
                    id={fieldId}
                    value={rule?.strategy ?? "preserve"}
                    onChange={(event) => onChangeRule(profile.name, { strategy: event.target.value as MaskStrategy })}
                  >
                    {(Object.keys(STRATEGY_LABELS) as MaskStrategy[]).map((strategy) => (
                      <option key={strategy} value={strategy}>
                        {STRATEGY_LABELS[strategy]}
                      </option>
                    ))}
                  </select>
                </td>
                <td>
                  {coverage?.covered ? (
                    <span className="state-badge state-badge--success">Sí</span>
                  ) : profile.piiCandidate ? (
                    <span className="state-badge state-badge--critical">No</span>
                  ) : (
                    <span className="state-badge state-badge--info">N/A</span>
                  )}
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
}
